import { useEffect, useMemo, useState } from "react";
import "./DataTable.css";

const DataTable = ({
  data = [],
  columns = [],
  onSelectionChange = () => {},
  selectedItems = [],
  onRowClick = null,
  searchable = true,
  sortable = true,
  selectable = false,
  defaultSort = { key: null, direction: "asc" },
  itemsPerPage = 10,
  showPagination = true,
  hover = true,
  striped = false,
  bordered = false,
  entries = 10,
  entriesOptions = [10, 25, 50, 100],
  searchPlaceholder = "Search here...",
  noDataMessage = "No data available",
  className = "",
  format = null,
}) => {
  const [sortConfig, setSortConfig] = useState(defaultSort);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(entries || itemsPerPage);

  // Sync with parent's selectedItems - only run for selectable tables
  useEffect(() => {
    if (!selectable) {
      return;
    }

    const selectedIds = new Set(selectedItems.map((item) => item.id));
    setSelectedRows(selectedIds);
    setSelectAll(selectedIds.size === data.length && data.length > 0);
  }, [selectedItems, selectable, data.length]);

  // Handle sorting
  const handleSort = (key) => {
    if (!sortable) return;

    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchable || !searchTerm) return data;

    return data.filter((row) => {
      return columns.some((column) => {
        const value = row[column.field || column.key];
        if (value === null || value === undefined) return false;
        return value
          .toString()
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      });
    });
  }, [data, searchTerm, columns, searchable]);

  // Memoize sorted data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      const column = columns.find(
        (col) => (col.field || col.key) === sortConfig.key,
      );
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle special sorting types
      if (column && column.sortType === "date") {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig, columns]);

  // Memoize paginated data
  const paginatedData = useMemo(() => {
    if (!showPagination) return sortedData;

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, showPagination, currentPage, pageSize]);

  // Calculate pagination info
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Reset page when search or pageSize changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  // Handle row selection
  const handleRowSelect = (rowId, e) => {
    if (!selectable) return;

    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const newSelectedRows = new Set(selectedRows);
    if (newSelectedRows.has(rowId)) {
      newSelectedRows.delete(rowId);
    } else {
      newSelectedRows.add(rowId);
    }
    setSelectedRows(newSelectedRows);

    // Update select all state
    const newSelectAll =
      newSelectedRows.size === filteredData.length && filteredData.length > 0;
    setSelectAll(newSelectAll);

    // Notify parent
    setTimeout(() => {
      const selectedItemsArray = data.filter((item) =>
        newSelectedRows.has(item.id),
      );
      onSelectionChange(selectedItemsArray);
    }, 0);
  };

  // Handle select all
  const handleSelectAll = (e) => {
    if (!selectable) return;

    e.preventDefault();
    e.stopPropagation();

    let newSelectedRows;
    let newSelectAll;

    if (selectAll) {
      newSelectedRows = new Set();
      newSelectAll = false;
    } else {
      newSelectedRows = new Set(filteredData.map((item) => item.id));
      newSelectAll = true;
    }

    setSelectedRows(newSelectedRows);
    setSelectAll(newSelectAll);

    // Notify parent
    setTimeout(() => {
      const selectedItemsArray = data.filter((item) =>
        newSelectedRows.has(item.id),
      );
      onSelectionChange(selectedItemsArray);
    }, 0);
  };

  // Get sort icon
  const getSortIcon = (columnKey) => {
    if (!sortable) return null;

    if (sortConfig.key !== columnKey) {
      return (
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="#6c757d"
          style={{ marginLeft: "4px" }}
        >
          <path d="M5 0 L10 5 L0 5 Z" opacity="0.3" />
          <path d="M5 10 L0 5 L10 5 Z" opacity="0.3" />
        </svg>
      );
    }

    return sortConfig.direction === "asc" ? (
      <svg
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill="#25afa8"
        style={{ marginLeft: "4px" }}
      >
        <path d="M5 0 L10 5 L0 5 Z" />
        <path d="M5 10 L0 5 L10 5 Z" opacity="0.3" />
      </svg>
    ) : (
      <svg
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill="#25afa8"
        style={{ marginLeft: "4px" }}
      >
        <path d="M5 0 L10 5 L0 5 Z" opacity="0.3" />
        <path d="M5 10 L0 5 L10 5 Z" />
      </svg>
    );
  };

  // Apply custom formatting to cell
  const getCellStyle = (column, value, row) => {
    if (format && typeof format === "function") {
      const field = column.field || column.key;
      return format(field, value);
    }
    return {};
  };

  // Handle row click
  const handleRowClick = (row, e) => {
    if (!onRowClick) return;

    // Don't trigger row click if clicking checkbox
    if (e.target.closest(".checkbox-column")) {
      return;
    }

    onRowClick(row, e);
  };

  const tableClasses = [
    "data-table",
    hover && "hover",
    striped && "striped",
    bordered && "bordered",
    onRowClick && "clickable",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="data-table-wrapper">
      {/* Search Box */}
      {searchable && (
        <div className="data-table-search">
          <input
            type="text"
            className="form-control"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      )}

      {/* Table */}
      <div className="data-table-container">
        <table className={tableClasses}>
          <thead>
            <tr>
              {selectable && (
                <th className="checkbox-column">
                  <div
                    className={`custom-checkbox ${selectAll ? "checked" : ""}`}
                    onClick={handleSelectAll}
                  >
                    {selectAll && (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="white"
                      >
                        <path
                          d="M10 3L4.5 8.5L2 6"
                          stroke="white"
                          strokeWidth="2"
                          fill="none"
                        />
                      </svg>
                    )}
                  </div>
                </th>
              )}
              {columns.map((column) => {
                const key = column.field || column.key;
                const label = column.label || column.title;
                const isSortable = column.sortable !== false && sortable;

                return (
                  <th
                    key={key}
                    className={isSortable ? "sortable" : ""}
                    onClick={() => isSortable && handleSort(key)}
                    style={{
                      ...(column.minWidth ? { minWidth: column.minWidth } : {}),
                      textAlign: column.headerAlign || "left"
                    }}
                  >
                    {label}
                    {isSortable && getSortIcon(key)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (selectable ? 1 : 0)}
                  className="no-data"
                >
                  {noDataMessage}
                </td>
              </tr>
            ) : (
              <>
                {paginatedData.map((row, index) => {
                  const rowId = row.id || index;
                  // Calculate actual row number across all pages
                  const actualIndex = (currentPage - 1) * pageSize + index;

                  return (
                    <tr
                      key={rowId}
                      className={selectedRows.has(rowId) ? "selected" : ""}
                      onClick={(e) => handleRowClick(row, e)}
                      style={{ cursor: onRowClick ? "pointer" : "default" }}
                    >
                      {selectable && (
                        <td className="checkbox-column">
                          <div
                            className={`custom-checkbox ${
                              selectedRows.has(rowId) ? "checked" : ""
                            }`}
                            onClick={(e) => handleRowSelect(rowId, e)}
                          >
                            {selectedRows.has(rowId) && (
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="white"
                              >
                                <path
                                  d="M10 3L4.5 8.5L2 6"
                                  stroke="white"
                                  strokeWidth="2"
                                  fill="none"
                                />
                              </svg>
                            )}
                          </div>
                        </td>
                      )}
                      {columns.map((column) => {
                        const key = column.field || column.key;
                        const value = row[key];
                        const cellStyle = getCellStyle(column, value, row);

                        return (
                          <td
                            key={key}
                            style={{
                              ...cellStyle,
                              textAlign: column.align || "left",
                            }}
                          >
                            {column.render
                              ? column.render(value, row, actualIndex)
                              : value}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {/* Add empty rows to maintain consistent height */}
                {showPagination &&
                  Array.from(
                    { length: Math.max(0, pageSize - paginatedData.length) },
                    (_, index) => (
                      <tr key={`empty-${index}`} className="empty-row">
                        {selectable && <td className="checkbox-column"></td>}
                        {columns.map((column) => (
                          <td
                            key={column.field || column.key}
                            style={{ textAlign: column.align || "left" }}
                          >
                            &nbsp;
                          </td>
                        ))}
                      </tr>
                    ),
                  )}
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {showPagination && totalPages > 0 && (
        <div className="data-table-pagination">
          <div className="pagination-entries">
            <label>Show</label>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="form-select form-select-sm"
            >
              {entriesOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <label>entries</label>
          </div>

          <div className="pagination-info">
            Showing {startItem} to {endItem} of {totalItems} entries
            {searchTerm && ` (filtered from ${data.length} total entries)`}
          </div>

          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            >
              First
            </button>
            <button
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            <span className="pagination-pages">
              Page {currentPage} of {totalPages || 1}
            </span>

            <button
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
            >
              Next
            </button>
            <button
              className="pagination-btn"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
            >
              Last
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
